import React, { useEffect, useState, useContext } from 'react'
import { Link } from 'react-router-dom'
import publicApi from '../services/public.api';
import LocationContext from '../context/LocationContext';
import { useCart } from '../context/CartContext';
import { addToCartAnimated } from '../utils/addToCartAnimated';
import ProductCardQuantity, { getDefaultCardSelection, isProductHasVariants } from './ProductCardQuantity';
import { buildCartProductFromSelection } from '../utils/cartProductHelpers';

// CSS styles for animated progress bar
const progressStyles = `
@keyframes progressPulse {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
    100% {
        opacity: 1;
    }
}

@keyframes progressShine {
    0% {
        left: -100%;
    }
    100% {
        left: 100%;
    }
}

@keyframes progressGrow {
    0% {
        width: 0%;
    }
}

.animated-progress-bar {
    position: relative;
    overflow: hidden;
    transition: width 1s ease-in-out;
    animation: progressPulse 2s ease-in-out infinite;
}

.animated-progress-bar::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
    );
    animation: progressShine 2s ease-in-out infinite;
}

.progress-animate-grow {
    animation: progressGrow 1.5s ease-out forwards;
}
`;

const ProductListOne = () => {
    // Dynamic products from API
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [animateProgress, setAnimateProgress] = useState(false);
    const [addedProductId, setAddedProductId] = useState(null);
    const [selections, setSelections] = useState({});
    const [weightHintId, setWeightHintId] = useState(null);
    
    // Cart context
    const { addToCart } = useCart();
    
    // Location-based pricing
    const context = useContext(LocationContext);
    const isCasaRegion = context?.isCasaRegion ?? false;
    const priceRegion = context?.priceRegion ?? 'fes';

    const getSelection = (product) =>
        selections[product.id] || getDefaultCardSelection(isProductHasVariants(product));

    const updateSelection = (product, next) => {
        setSelections((prev) => ({ ...prev, [product.id]: next }));
    };

    const handleAddToCart = (e, product) => {
        e.preventDefault();
        e.stopPropagation();
        const basePrice = getLocationPrice(product);
        const built = buildCartProductFromSelection(product, basePrice, getSelection(product));
        if (built.error) {
            setWeightHintId(product.id);
            setTimeout(() => setWeightHintId(null), 2000);
            return;
        }
        addToCartAnimated({
            event: e,
            product: built.product,
            addToCart,
            quantity: built.quantity,
        });
        setAddedProductId(product.id);
        setTimeout(() => setAddedProductId(null), 1500);
    };
    
    // Debug log on every render
    console.log('[ProductListOne] RENDER - isCasaRegion:', isCasaRegion, '| priceRegion:', priceRegion);
    
    // Get price based on location
    const getLocationPrice = (product) => {
        if (!product) return 0;
        const priceCasa = parseFloat(product.price_casa || 0);
        const regularPrice = parseFloat(product.price || 0);
        const result = (isCasaRegion && priceCasa > 0) ? priceCasa : regularPrice;
        console.log('[ProductListOne] getLocationPrice:', product.name, '| casa:', priceCasa, '| regular:', regularPrice, '| isCasaRegion:', isCasaRegion, '| RESULT:', result);
        return result;
    };

    // Fetch featured products from API
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await publicApi.getFeaturedProducts();
                if (response.success && response.data.length > 0) {
                    setProducts(response.data);
                }
            } catch (error) {
                console.error('Error fetching featured products:', error);
            } finally {
                setLoading(false);
                // Trigger animation after loading
                setTimeout(() => setAnimateProgress(true), 100);
            }
        };

        fetchProducts();
    }, []);

    // Inject styles into document
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = progressStyles;
        document.head.appendChild(styleElement);
        
        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    // Fallback static products (original)
    const staticProducts = [
        { id: 1, name: 'Miel de Daghmous', image: 'assets/images/thumbs/product-img1.png', weight: '500g', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
        { id: 2, name: 'Miel aux herbes', image: 'assets/images/thumbs/product-img2.png', weight: '300g', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
        { id: 3, name: 'Miel de nigelle', image: 'assets/images/thumbs/product-img3.png', weight: '1kg', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
        { id: 4, name: 'Miel de caroubier', image: 'assets/images/thumbs/Mielauxherbes.png', weight: '750g', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
        { id: 5, name: 'Miel de thym', image: 'assets/images/thumbs/product-img5.png', weight: '400g', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
        { id: 6, name: 'Miel de jujubier', image: 'assets/images/thumbs/product-img6.png', weight: '600g', price: 14.99, original_price: 28.99, rating_average: 4.8, rating_count: 17000, sold_count: 18, initial_stock: 35, sold_percentage: 51 },
    ];

    // Use dynamic products if available, otherwise use static
    const displayProducts = products.length > 0 ? products : staticProducts;

    // Get product image URL
    const getProductImageUrl = (product) => {
        // If from API (has image path)
        if (product.image && !product.image.startsWith('assets/')) {
            return publicApi.getImageUrl(product.image);
        }
        // Static image
        return product.image || 'assets/images/thumbs/product-img1.png';
    };

    // Format rating count (e.g., 17000 -> 17k)
    const formatRatingCount = (count) => {
        if (count >= 1000) {
            return `${(count / 1000).toFixed(0)}k`;
        }
        return count.toString();
    };

    // Format price to 2 decimal places
    const formatPrice = (price) => {
        return parseFloat(price).toFixed(2);
    };

    // Loading skeleton - same design as product card
    if (loading) {
        return (
            <div className="product mt-24">
                <div className="container container-lg">
                    <div className="row gy-4 g-12">
                        {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div key={i} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xxl-2">
                                <div className="product-card px-8 py-16 border border-gray-100 rounded-16 position-relative">
                                    <div className="skeleton-loader" style={{ height: '150px', background: '#f0f0f0', borderRadius: '8px' }}></div>
                                    <div className="mt-12">
                                        <div className="skeleton-loader" style={{ height: '20px', width: '60%', background: '#f0f0f0', borderRadius: '4px', marginBottom: '8px' }}></div>
                                        <div className="skeleton-loader" style={{ height: '16px', width: '40%', background: '#f0f0f0', borderRadius: '4px' }}></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="product mt-24" key={`products-${priceRegion}`}>
            <div className="container container-lg">
                <div className="row gy-4 g-12">
                    {displayProducts.map((product) => (
                        <div key={`${product.id}-${priceRegion}`} className="col-12 col-sm-6 col-md-4 col-lg-3 col-xxl-2">
                        <div className="product-card px-8 py-16 border border-gray-100 product-list-hover rounded-16 position-relative transition-2 w-100 h-100 d-flex flex-column">
                            <Link
                                to={`/product-details/${product.id}`}
                                className="product-card__thumb flex-center"
                            >
                                <img 
                                    src={getProductImageUrl(product)} 
                                    alt={product.name}
                                    onError={(e) => {
                                        e.target.src = 'assets/images/thumbs/product-img1.png';
                                    }}
                                />
                            </Link>
                            <div className="product-card__content mt-12">
                                <div className="flex-between align-items-start gap-8 mb-12">
                                    <div className="product-card__price">
                                        <div className="flex-align gap-8 flex-wrap">
                                            {/* Show original price (crossed out) only for Fes region if original_price exists */}
                                            {(!isCasaRegion && product.original_price) && (
                                                <span className="text-gray-400 text-md fw-semibold text-decoration-line-through">
                                                    {formatPrice(product.original_price)} DH
                                                </span>
                                            )}
                                            {/* Show current location-based price */}
                                            <span className="text-heading text-md fw-semibold">
                                                {formatPrice(
                                                    isProductHasVariants(product) && getSelection(product).weightValue
                                                        ? getLocationPrice(product) * getSelection(product).weightValue
                                                        : getLocationPrice(product)
                                                )} DH
                                            </span>
                                        </div>
                                        <span className="text-gray-500 text-sm fw-normal d-block mt-4">
                                            {isProductHasVariants(product)
                                                ? (getSelection(product).weightLabel || 'Qty')
                                                : (product.weight || 'Qty')}
                                        </span>
                                        {weightHintId === product.id && (
                                            <span className="text-danger text-xs fw-medium d-block mt-4">
                                                Choisissez un poids
                                            </span>
                                        )}
                                    </div>
                                    <ProductCardQuantity
                                        hasVariants={isProductHasVariants(product)}
                                        selection={getSelection(product)}
                                        onChange={(next) => updateSelection(product, next)}
                                        collapsedUntilInteraction
                                    />
                                </div>
                                <div className="flex-align gap-6">
                                    <span className="text-xs fw-bold text-gray-600">
                                        {product.rating_average || 4.8}
                                    </span>
                                    <span className="text-15 fw-bold text-warning-600 d-flex">
                                        <i className="ph-fill ph-star" />
                                    </span>
                                    <span className="text-xs fw-bold text-gray-600">
                                        ({formatRatingCount(product.rating_count || 0)})
                                    </span>
                                </div>
                                <h6 className="title text-lg fw-semibold mt-12 mb-8">
                                    <Link to={`/product-details/${product.id}`} className="link text-line-2 product-list-name-link">
                                        {product.name}
                                    </Link>
                                </h6>
                                <div className="flex-align gap-4">
                                    <span className="text-main-600 text-md d-flex">
                                        <i className="ph-fill ph-storefront" />
                                    </span>
                                    <span className="text-gray-500 text-xs">
                                        Groupe Délices Benkirane
                                    </span>
                                </div>
                                <div className="mt-12">
                                    <div
                                        className="progress w-100 bg-color-three rounded-pill h-4"
                                        role="progressbar"
                                        aria-label="Sold progress"
                                        aria-valuenow={product.sold_percentage || 35}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                        style={{ position: 'relative', overflow: 'hidden' }}
                                    >
                                        <div
                                            className={`progress-bar product-list-progress rounded-pill animated-progress-bar ${animateProgress ? 'progress-animate-grow' : ''}`}
                                            style={{ 
                                                width: animateProgress ? `${product.sold_percentage || 35}%` : '0%',
                                                transition: 'width 1.5s ease-out'
                                            }}
                                        />
                                    </div>
                                    <span className="text-gray-900 text-xs fw-medium mt-8">
                                        Sold: {product.sold_count || 0}/{product.initial_stock || (product.stock + (product.sold_count || 0))}
                                    </span>
                                </div>
                                <button
                                    onClick={(e) => handleAddToCart(e, product)}
                                    disabled={addedProductId === product.id}
                                    className={`product-card__cart product-list-add-btn-mobile btn ${
                                        addedProductId === product.id 
                                            ? 'btn-success' 
                                            : 'product-list-add-btn'
                                    } text-white hover-text-white py-11 px-24 rounded-pill flex-align gap-8 w-100 justify-content-center mt-16`}
                                    style={{
                                        transition: 'all 0.3s ease',
                                        border: 'none',
                                        cursor: addedProductId === product.id ? 'default' : 'pointer'
                                    }}
                                >
                                    {addedProductId === product.id ? (
                                        <>Ajouté <i className="ph ph-check-circle" /></>
                                    ) : (
                                        <>Ajouter au Panier <i className="ph ph-shopping-cart" /></>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                    ))}
                </div>
            </div>
        </div>

    )
}

export default ProductListOne